import { ICommonSettings, ISerperToolSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigSerperToolSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingSerperTool?: ISerperToolSetting;
}

export interface IConfigSerperToolSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingSerperToolId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigSerperToolSettings extends React.PureComponent<
  IConfigSerperToolSettingsProps,
  IConfigSerperToolSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigSerperToolSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSerperToolSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigSerperToolSettingsProps) {
    if (prevProps.editingSerperToolId !== this.props.editingSerperToolId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSerperToolSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSerperToolSetting();
    }
  }

  loadSerperToolSetting = () => {
    if (!this.props.editingSerperToolId) return;

    const editingSerperTool = this.props.commonSettings.serperToolSettings?.find(
      (x) => x.id === this.props.editingSerperToolId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingSerperTool) return;
    this.setState({ editingSerperTool }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingSerperTool);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const SerperToolsSettings = this.props.commonSettings?.serperToolSettings;

    const isSettingAlreadyExist =
      SerperToolsSettings?.some(
        (x) => x.name === value && x.id !== this.props.editingSerperToolId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Serper Tool Settings Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();

    let obj: ISerperToolSetting;
    if (this.props.editingSerperToolId) {
      obj = {
        id: this.props.editingSerperToolId,
        name: data.name,
        apiKey: data.apiKey
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "serperToolSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        apiKey: data.apiKey?.trim()
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "serperToolSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSerperTool = () => {
    if (!this.props.editingSerperToolId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingSerperToolId || "",
      configKey: "serperToolSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Serper Tool setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Serper Tool Setting"
        secondHeader="Serper Tool Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingSerperToolId ? this.deleteSerperTool : undefined}
            submitLabel={this.props.editingSerperToolId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configSerperToolForm"
          />
        }
      >
        <Form
          id="configSerperToolForm"
          name="SerperToolSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_serpertoolsettingpop_up_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="appsettings_serpertoolsettingpop_up_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Serper Tool Setting Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Serper Tool Setting Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Api Key" required tooltipContentKey="appsettings_serpertoolsettingpop_up_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Api Key." }]}>
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
